import Vue from 'vue';
import _ from 'lodash';
import { applyPatch, compare, deepClone } from 'fast-json-patch';
import data from './data/data.js';
import config from './config/config.js';
import users from './users.js';
import { trigger, filterNonTriggered } from './triggers.js';

// import transform from './transforms';

const state = {
  name: null,
  hash: null,
  publicLink: false,
  status: null,
  externalUsers: [],
  ownerId: null,
  cover: null,
  coverUrl: null,
  workspaceId: null,
};

const getters = {
  /**
   * Returns the project name. (Useful if the user does not have the open project in his list - public project)
   * @returns {String}  The project name
   */
  getName(state) {
    return state.name;
  },

  /**
   * Returns the project hash.
   * @returns {String}  The project hash
   */
  getHash(state) {
    return state.hash;
  },

  /**
   * Returns the project public link property. (Useful if the user does not have the open project in his list - public project)
   * @returns {String|null} The public link property
   */
  getPublicLink(state) {
    return state.publicLink;
  },

  /**
   * Returns the project data.
   * @returns {Object}  The project data
   */
  getData(state) {
    return state.data;
  },

  /**
   * Returns the project config.
   * @returns {Object}  The project config
   */
  getConfig(state) {
    return state.config;
  },

  /**
   * Returns an object containing data and config
   * @param {} state
   */
  getState(state) {
    return { data: state.data, config: state.config };
  },

  /**
   * Returns the status of the project (active, inactive, uploading)
   * @returns {String} The project status, one of ['active', 'inactive', 'uploading']
   */
  getStatus(state) {
    return state.status;
  },

  /**
   * Returns the external users of the project
   * @returns {Array}
   */
  getExternalUsers(state) {
    return state.externalUsers;
  },

  /**
   * Returns the project owner's ID
   * @returns {Number}
   */
  getOwnerId(state) {
    return state.ownerId;
  },

  getCover(state) {
    return state.cover;
  },

  getCoverUrl(state) {
    return state.coverUrl;
  },

  getWorkspaceId(state) {
    return state.workspaceId;
  },
};

const actions = {
  setData(context, { value }) {
    context.commit('setData', { value });
  },
};

const mutations = {
  /**
   * Set the state data
   * @param {Object} value  The new state data
   */
  setData(state, { value }) {
    state.data = value;
  },

  /**
   * Set the state config
   * @param {Object} value  The new state config
   */
  setConfig(state, { value }) {
    state.config = value;
  },

  /**
   * Set the name
   * @param {String} value  The name
   */
  setName(state, { value }) {
    state.name = value;
  },

  /**
   * Set the hash
   * @param {String} value  The hash
   */
  setHash(state, { value }) {
    state.hash = value;
  },

  /**
   * Set the public link property
   * @param {string} value The public link property
   */
  setPublicLink(state, { value }) {
    state.publicLink = value;
  },

  /**
   * Set the project's external users
   * @param {Array} value The external users object
   */
  setExternalUsers(state, { value }) {
    state.externalUsers = value || [];
  },

  /**
   * Set the project owner ID
   * @param {number} value The owner's ID
   */
  setOwnerId(state, { value }) {
    state.ownerId = value;
  },

  /**
   * Apply a JSON patch to the project state
   */
  updateProject(state, { patch }) {
    applyPatch(state, patch, true);
  },

  setCover(state, { value }) {
    state.cover = value;
  },

  setCoverUrl(state, { value }) {
    state.coverUrl = value;
  },

  setWorkspaceId(state, { value }) {
    state.workspaceId = value;
  },
};

const modules = {
  data,
  config,
  users,
};

const module = {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
  modules,
};

export default module;
